import { FormEventHandler, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageContent, PageHeader, PageLayout } from "@/components/page";
import {
  useCreateObservation,
  useUpdateObservation,
  useObservation,
  useRacks,
  useTanks,
} from "@/hooks";
import RackObservationForm from "./Form";
import RackPhoto from "./RackPhoto";
import { Annotorious, useAnnotations, useAnnotator } from "@annotorious/react";
import { EmptyState } from "@/components/ui/empty-state";
import { MdHighlightOff } from "react-icons/md";
import { AbsoluteCenter, Box, Skeleton } from "@chakra-ui/react";
import { DeviceOrientationEventiOS, FormSteps, IObservation } from "@/types/data";
import LiveCamera from "./LiveCamera";
import { useImageCompression } from "@/hooks";
import { useFormSteps } from "@/hooks";

const S3_BASE_URL =
  "https://tnccoralecsstackstaging-tnccoralstagingbucket20962-sh31zmzezifj.s3.us-west-1.amazonaws.com/uploads/";

type RouteParams = {
  nurseryId: string;
  tankId?: string;
  rackId?: string;
};

type ObservationRouteParams = {
  nurseryId: string;
  tankId: string;
  rackId: string;
  observationId: string;
};


function RackObservationCreationForm() {
  const navigate = useNavigate();
  const {
    nurseryId,
    tankId: initialTankId,
    rackId: initialRackId,
  } = useParams<RouteParams>();
  const [rackId, setRackId] = useState<string>(initialRackId || "");
  const [tankId, setTankId] = useState<string>(initialTankId || "");
  const { createObservation, createIsPending, createError } =
    useCreateObservation(nurseryId!, rackId!);

  const { formStep, setFormStep } = useFormSteps();
  const [note, setNote] = useState<string>("");
  const [image, setImage] = useState<File>();
  const { compressedImage } = useImageCompression(image);

  const { data: racks, isPending: racksLoading } = useRacks(
    nurseryId!,
    tankId!,
    !!tankId,
  );
  const { data: tanks, isPending: tanksLoading } = useTanks(nurseryId!);
  const annotations = useAnnotations(1);
  const tankData = { tankId, setTankId, tanks: tanks?.results, tanksLoading };
  const rackData = { rackId, setRackId, racks: racks?.results, racksLoading };

  const [orientationPermission, setOrientationPermission] = useState<
    "granted" | "denied" | undefined
  >(undefined);
  const [orientation, setOrientation] =
    useState<DeviceOrientationEventiOS | null>(null);
  const requestPermission = (
    DeviceOrientationEvent as unknown as DeviceOrientationEventiOS
  ).requestPermission;
  const iOS = typeof requestPermission === "function";

  // Non-iOS browsers (Android, desktop) fire deviceorientation without any
  // permission prompt, so auto-grant for them.
  useEffect(() => {
    if (!iOS && window.DeviceOrientationEvent) {
      setOrientationPermission("granted");
    }
  }, [iOS]);

  useEffect(() => {
    if (orientationPermission === "granted" && window.DeviceOrientationEvent) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        setOrientation({
          alpha: event.alpha,
          beta: event.beta,
          gamma: event.gamma,
          absolute: event.absolute,
        } as unknown as DeviceOrientationEventiOS);
      };

      window.addEventListener("deviceorientation", handleOrientation, true);
      return () => {
        window.removeEventListener(
          "deviceorientation",
          handleOrientation,
          true,
        );
      };
    }
  }, [orientationPermission]);

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    if (!tankId) {
      alert("Please select a rack.");
      return;
    }
    if (!rackId) {
      alert("Please select a rack.");
      return;
    }
    createObservation(
      { note, annotations, image: compressedImage },
      {
        onSuccess: () => navigate(`/nurseries/${nurseryId}/tanks/${tankId}/`),
      },
    );
  };

  return (
    <PageLayout>
      <PageHeader
        backLink={
          rackId
            ? `/nurseries/${nurseryId}/tanks/${tankId}/racks/${rackId}`
            : `/nurseries/${nurseryId}`
        }
        bg={{ base: "black/75", sm: "white" }}
        color={{ base: "white", sm: "initial" }}
        borderBottomColor={{ base: "black", sm: "border" }}
        mb={0}
      >
        New Observation{" "}
        {new Date().toLocaleString([], {
          dateStyle: "short",
          timeStyle: "short",
        })}
      </PageHeader>
      <PageContent fluid p={0} gridRow="2 / -1" maxW="none">
        <>
          {formStep === FormSteps.Photo && (
            <AbsoluteCenter>
              <RackPhoto
                onOpenCamera={async () => {
                  setFormStep(FormSteps.LiveCamera);
                  // prompt user to grant orientation permission on iOS
                  if (iOS) {
                    const response = await requestPermission();
                    if (response === "granted") {
                      setOrientationPermission("granted");
                    }
                  }
                }}
                setImage={(f) => {
                  setImage(f);
                  setFormStep(FormSteps.ObservationForm);
                }}
                backLink={
                  rackId
                    ? `/nurseries/${nurseryId}/tanks/${tankId}/racks/${rackId}`
                    : `/nurseries/${nurseryId}`
                }
              />
            </AbsoluteCenter>
          )}
          {formStep === FormSteps.LiveCamera && (
            <LiveCamera
              onCapture={(f) => {
                setImage(f);
                setFormStep(FormSteps.ObservationForm);
              }}
              onBack={() => setFormStep(FormSteps.Photo)}
              requestOrientationPermission={
                iOS && orientationPermission !== "granted"
              }
              orientationPermissionCallback={async () => {
                if (iOS) {
                  const response = await requestPermission();
                  if (response === "granted") {
                    setOrientationPermission("granted");
                  }
                }
              }}
              onUpload={(f) => {
                setImage(f);
                setFormStep(FormSteps.ObservationForm);
              }}
              orientation={orientation}
            />
          )}
          {formStep === FormSteps.ObservationForm && (
            <>
              <RackObservationForm
                nurseryId={nurseryId!}
                tankData={tankData}
                rackData={rackData}
                note={note}
                setNote={setNote}
                image={image}
                handleSubmit={handleSubmit}
                createError={createError}
                createIsPending={createIsPending}
                updateMode={false}
              />
            </>
          )}
        </>
      </PageContent>
    </PageLayout>
  );
}

function RackObservationUpdateForm({ data }: { data: IObservation }) {
  const navigate = useNavigate();
  const { nurseryId, tankId, rackId, observationId } =
    useParams<ObservationRouteParams>();
  const { updateObservation, updateIsPending, updateError } =
    useUpdateObservation(nurseryId!, observationId!);
  const { data: racks } = useRacks(nurseryId!, tankId!);
  const { data: tanks } = useTanks(nurseryId!);

  const [note, setNote] = useState<string>(data?.note || "");
  const annotations = useAnnotations(1);
  const anno = useAnnotator();

  useEffect(() => {
    if (anno && data.annotations) anno.setAnnotations(data.annotations);
  }, [anno, data]);

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    updateObservation(
      { note, annotations },
      {
        onSuccess: () => navigate(`/nurseries/${nurseryId}/tanks/${tankId}/`),
      },
    );
  };
  const tankData = { tankId, tanks: tanks?.results };
  const rackData = { rackId, racks: racks?.results };

  return (
    <PageLayout>
      <PageHeader
        backLink={`/nurseries/${nurseryId}/tanks/${tankId}/racks/${rackId}`}
        bg={{ base: "black/75", sm: "white" }}
        color={{ base: "white", sm: "initial" }}
        borderBottomColor={{ base: "black", sm: "border" }}
        mb={0}
      >
        Observation #{observationId}{" "}
        {new Date(data?.created).toLocaleString([], {
          dateStyle: "short",
          timeStyle: "short",
        })}
      </PageHeader>
      <PageContent
        fluid
        p={0}
        gridRow={{ base: "2 / -1", md: "initial" }}
        maxW="none"
      >
        <RackObservationForm
          nurseryId={nurseryId!}
          tankData={tankData}
          rackData={rackData}
          note={note}
          setNote={setNote}
          createdDate={data.created}
          image={`${S3_BASE_URL}${data.image}`}
          handleSubmit={handleSubmit}
          createError={updateError}
          createIsPending={updateIsPending}
          updateMode={true}
          status={data.status}
        />
      </PageContent>
    </PageLayout>
  );
}

function RackObservationCreation() {
  return (
    <Annotorious>
      <RackObservationCreationForm />
    </Annotorious>
  );
}

function RackObservationUpdate() {
  const { nurseryId, observationId } = useParams<ObservationRouteParams>();
  const { data, isPending, error } = useObservation(nurseryId!, observationId!);

  return (
    <>
      {error && (
        <EmptyState
          icon={<MdHighlightOff />}
          title="Error"
          description={error.message}
        />
      )}
      {isPending && (
        <>
          <Skeleton h={10} m={4} borderRadius="md" />
          <Box
            display={{ base: "flex", md: "grid" }}
            flexDirection="column"
            gridTemplateColumns={{
              base: "minmax(0px, 1fr)",
              md: "minmax(0px, 1fr) 20rem",
            }}
            gridTemplateRows="minmax(0px, max-content) minmax(0px, 1fr)"
            gap={4}
            p={4}
            pt={0}
            height="100%"
            minH="calc(100vh - 5rem)"
            maxH="calc(100vh - 5rem)"
            overflow="hidden"
          >
            <Skeleton
              h="100%"
              w="100%"
              gridColumn="1"
              gridRow="1 / -1"
              borderRadius="md"
            />
            <Skeleton
              h="100%"
              w="100%"
              gridColumn="2"
              gridRow="1 / -1"
              borderRadius="md"
            />
          </Box>
        </>
      )}
      {data && (
        <Annotorious>
          <RackObservationUpdateForm data={data} />
        </Annotorious>
      )}
    </>
  );
}

export { RackObservationCreation, RackObservationUpdate };
